import { useCallback, useEffect, useState } from 'react';

/**
 * Theme preference stored in localStorage. `null` means "follow system",
 * which is also the default for first-time visitors.
 */
export type ThemePref = 'light' | 'dark' | null;

const STORAGE_KEY = 'theme-pref';
const CHANGE_EVENT = 'cb:theme-change';

function readStored(): ThemePref {
  if (typeof window === 'undefined') return null;
  const v = window.localStorage.getItem(STORAGE_KEY);
  return v === 'light' || v === 'dark' ? v : null;
}

function systemPrefersDark(): boolean {
  if (typeof window === 'undefined' || !window.matchMedia) return false;
  return window.matchMedia('(prefers-color-scheme: dark)').matches;
}

/** Apply a `data-theme` attribute on <html>; `null` removes it (= follow OS). */
function applyTheme(pref: ThemePref) {
  const root = document.documentElement;
  if (pref === null) {
    root.removeAttribute('data-theme');
  } else {
    root.setAttribute('data-theme', pref);
  }
}

/**
 * useTheme — small hook that owns the user's light/dark preference.
 *
 * - `pref` is the stored choice (light, dark, or null = system)
 * - `resolved` is the actual theme currently in effect (light or dark)
 * - `toggle()` flips between light and dark, persisting the choice
 */
export function useTheme() {
  const [pref, setPref] = useState<ThemePref>(() => readStored());
  const [systemDark, setSystemDark] = useState<boolean>(() => systemPrefersDark());

  // Keep <html data-theme> in sync with the preference.
  useEffect(() => {
    applyTheme(pref);
  }, [pref]);

  // Track OS-level scheme changes so `resolved` stays accurate when
  // the user is on the system default.
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = (e: MediaQueryListEvent) => setSystemDark(e.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  // Keep multiple instances of the toggle in sync. When one instance
  // changes the preference, every mounted ThemeToggle receives the
  // new value and re-renders.
  useEffect(() => {
    const onChange = (e: Event) => {
      const detail = (e as CustomEvent<ThemePref>).detail;
      setPref(detail);
    };
    window.addEventListener(CHANGE_EVENT, onChange);
    return () => window.removeEventListener(CHANGE_EVENT, onChange);
  }, []);

  const resolved: 'light' | 'dark' = pref ?? (systemDark ? 'dark' : 'light');

  const setTheme = useCallback((next: ThemePref) => {
    if (next === null) {
      window.localStorage.removeItem(STORAGE_KEY);
    } else {
      window.localStorage.setItem(STORAGE_KEY, next);
    }
    setPref(next);
    window.dispatchEvent(new CustomEvent<ThemePref>(CHANGE_EVENT, { detail: next }));
  }, []);

  const toggle = useCallback(() => {
    setTheme(resolved === 'dark' ? 'light' : 'dark');
  }, [resolved, setTheme]);

  return { pref, resolved, setTheme, toggle };
}
